<template>
  <div>
    <v-card
      flat
      width="100%"
    >
      <v-card
        class="d-flex justify-space-between mb-1 mt-1"
      >
        <v-card class="d-flex justify-center flex-grow-1" flat>
          <v-card flat class="pa-2">
            <v-text-field
              prepend-inner-icon="mdi-magnify"
              clearable
              :label="$t('message.common.fid')"
              v-model="search_fid"
            >
            </v-text-field>
          </v-card>
          <v-card flat class="pa-2">
            <v-text-field
              prepend-inner-icon="mdi-magnify"
              clearable
              :label="$t('message.file.file')"
              v-model="search_file"
            >
            </v-text-field>
          </v-card>
        </v-card>
        <v-card class="align-self-center mr-1" flat>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="ma-2" @click="get_data('file', []);">
                mdi-refresh
              </v-icon>
            </template>
            <span>{{ $t("message.common.refresh") }}</span>
          </v-tooltip>
          <template v-if="upload_refer !== null">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click="$router.push('/file/upload?referer=/' + upload_refer)">
                  mdi-file-upload
                </v-icon>
              </template>
              <span>{{ $t("message.common.upload") }}</span>
            </v-tooltip>
          </template>
        </v-card>
      </v-card>
    </v-card>
    <v-data-table
      :value="selected_file"
      item-key="fid"
      :headers="headers"
      :items="data"
      :sort-by="sort_by"
      :sort-desc="sort_desc"
      must-sort
      class="elevation-1"
      :single-select="!multiple"
      :show-select="show_select"
      :search="extension_type"
      :custom-filter="custom_filter"
      @input="show_select ? $emit('update_files_select', $event) : ''"
      @click:row="show_select ? '' : $emit('update_files_select', [$event])"
      :footer-props="{
        'items-per-page-options': [10, 100, 200, 500]
      }"
    >
      <template v-slot:header.data-table-select />
      <template v-slot:item.fid="{ item }">
        {{ item.fid }}
      </template>
      <template v-slot:item.name="{ item }">
        <template v-if="item.name.length <= 40">
          {{ item.name }}
        </template>
        <template v-else>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ item.name.substr(0, 40) }}...
              </span>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>
        </template>
      </template>
      <template v-slot:item.type="{ item }">
        {{ $t('message.file.file_type_' + item.type) }}
      </template>
      <template v-slot:item.size="{ item }">
        {{ (item.size / 1024).toFixed(1) | comma_digit }}
      </template>
      <template v-slot:item.ctime="{ item }">
        {{ $d(1000 * item.ctime, 'long', $store.getters.langs[$i18n.locale].locale) }}
      </template>
      <template v-slot:item.remarks="{ item }">
        <FileRemark
          :key="item.fid + '_remarks'"
          :remarks="item.remarks"
          :mode="mode"
          :nav_path="null"
          :visible="[]"
        >
        </FileRemark>
      </template>
      <template v-slot:item.operation="{ item }">
        <v-menu
          offset-y
          v-if="
            item.type !== 'similarity'
            && downloadable(item.extensions).length > 0
            && operations.indexOf('download') >= 0
          "
        >
          <template v-slot:activator="{ on: onMenu }">
            <v-tooltip top>
              <template v-slot:activator="{ on: onTooltip }">
                <v-icon
                  class="ma-2"
                  v-on="{ ...onMenu, ...onTooltip }"
                  @click.stop
                >
                  mdi-download
                </v-icon>
              </template>
              <span>{{ $t("message.file.download") }}</span>
            </v-tooltip>
          </template>

          <v-list v-for="extension in downloadable(item.extensions)" :key="extension">
            <v-list-item dense @click="get_download(item.fid, extension)">
              <v-list-item-content>
                {{ $t('message.file.download_as', { "extension": $t('message.common.extension_' + extension) } ) }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-menu
          offset-y
          v-if="
            item.type !== 'similarity'
            && previewable(item.extensions).length > 0
            && operations.indexOf('preview') >= 0
          "
        >
          <template v-slot:activator="{ on: onMenu }">
            <v-tooltip top>
              <template v-slot:activator="{ on: onTooltip }">
                <v-icon
                  class="ma-2"
                  v-on="{ ...onMenu, ...onTooltip }"
                  @click.stop
                >
                  mdi-file-find
                </v-icon>
              </template>
              <span>{{ $t("message.viewer.preview") }}</span>
            </v-tooltip>
          </template>

          <v-list v-for="extension in previewable(item.extensions)" :key="extension">
            <template v-if="['txt', 'docx', 'xlsx', 'pptx', 'pdf', 'ocr', 'json'].indexOf(extension) >= 0">
              <v-list-item dense @click="$refs.viewer && $refs.viewer.open(item.fid, extension)">
                <v-list-item-content>
                  {{ $t('message.viewer.preview_as', { "extension": $t('message.common.extension_' + extension) } ) }}
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <template v-if="
          mode === 'user'
          && item.type === 'source'
        ">
          <template v-if="
            operations.indexOf('translate_this') >= 0
          ">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click.stop="$router.push('/translate?source=' + item.fid)">
                  mdi-translate
                </v-icon>
              </template>
              <span>{{ $t("message.file.translate_this") }}</span>
            </v-tooltip>
          </template>
          <!--
          <template v-if="
            item.extensions.indexOf('json') >= 0
            && deletable(item)
            && operations.indexOf('edit_source') >= 0
          ">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click.stop="$router.push('/editor/source/' + item.fid + get_referer('file'))">
                  mdi-book-open
                </v-icon>
              </template>
              <span>{{ $t("message.file.edit_source") }}</span>
            </v-tooltip>
          </template>
          -->
        </template>
        <template v-else-if="
          mode === 'user'
          && item.type === 'translated'
          && item.extensions.indexOf('json') >= 0
          && deletable(item)
          && operations.indexOf('edit_translated') >= 0
        ">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="ma-2" @click.stop="$router.push('/editor/translated/' + item.fid + get_referer('file'))">
                mdi-book-open
              </v-icon>
            </template>
            <span>{{ $t("message.file.edit_translated") }}</span>
          </v-tooltip>
        </template>
        <template v-if="
          deletable(item)
          && operations.indexOf('delete') >= 0
        ">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="ma-2" @click.stop="confirm_delete_data(get_prefix() + 'file', [item.fid], get_id())">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t("message.common.delete") }}</span>
          </v-tooltip>
        </template>
      </template>
      <template v-slot:footer.prepend>
        <v-card v-if="multiple" flat class="pl-3">
          {{ $t("message.common.selecting", { cur: selected_file.length, max: max_select }) }}
          <template v-if="select_one && selected_file.length < 1">
            <br />
            <span class="red--text">
              ({{ $t("message.common.select_one") }})
            </span>
          </template>
          <template v-if="selected_file.length > max_select">
            <br />
            <span class="red--text">
              ({{ $t("message.common.over_selecting", { max: max_select }) }})
            </span>
          </template>
        </v-card>
      </template>
    </v-data-table>
    <Viewer
      ref="viewer"
      :mode="mode"
    >
    </Viewer>
    <Confirm
      ref="delete_confirm"
      :message="$t('message.common.delete_confirm')"
      :positive="$t('message.common.yes')"
      :negative="$t('message.common.no')"
    >
    </Confirm>
    <v-overlay
      :absolute="true"
      :value="progress_overlay"
    >
      <v-progress-circular
        :value="progress"
      >
        {{ progress }}
      </v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Viewer from '../components/Viewer';
import FileRemark from '../components/FileRemark';
import Confirm from '../components/Confirm';
import { res_list } from "../js/res_list";
import { http_error } from "../js/http_error";
import { get_download } from "../js/get_download";

export default {
  name: 'file_select',
  components: {
    FileRemark,
    Viewer,
    Confirm
  },
  mixins: [res_list, http_error, get_download],
  props: {
    selected_file: Array,
    file_type: [String, Array],
    extension_type: String,
    own_only: Boolean,
    multiple: Boolean,
    select_one: Boolean,
    max_select: Number,
    show_select: Boolean,
    initital_fid: String,
    operations: {
      type: Array,
      default: () => { return ['preview']; }
    },
    upload_refer: {
      type: String,
      default: null
    },
    lang_pair: {
      type: Array,
      default: null
    }
  },
  data: function(){
    return {
      module_id: "73",
      data: [],
      mode: "user",
      progress: 0,
      progress_overlay: false,
      search_fid: "",
      search_file: "",
      sort_by: ["ctime"],
      sort_desc: true
    }
  },
  mounted: function(){
    this.search_fid = this.initital_fid;
    this.get_data("file", []);
  },
  methods: {
    custom_filter(value, search, item) {
      var own_only = false;
      if(this.own_only === null || this.own_only === undefined){
        own_only = true;
      }else if(this.own_only){
        own_only = (item.uid === this.$store.getters.user.uid);
      }else{
        own_only = true;
      }
      var extentions_available = false;
      if(search === null || search === undefined){
        extentions_available = true;
      }else{
        extentions_available = (item.extensions.indexOf(search) >= 0);
      }
      return own_only && extentions_available;
    }
  },
  computed: {
    headers: function() {
      return [
        {
          text: this.$i18n.t("message.common.fid"),
          value: 'fid',
          filter: value => {
            if(this.search_fid === "" || this.search_fid === null){
              return true;
            }
            return value.indexOf(this.search_fid) !== -1;
          }
        },
        {
          text: this.$i18n.t("message.file.file"),
          value: 'name',
          filter: value => {
            if(this.search_file === "" || this.search_file === null){
              return true;
            }
            return value.indexOf(this.search_file) !== -1;
          }
        },
        {
          text: this.$i18n.t("message.common.type"),
          value: 'type',
          sortable: false,
          filter: value => {
            if(typeof this.file_type === "string"){
              if(this.file_type === "all"){
                return true;
              }
              return this.file_type === value;
            }else{
              // file_type is Array
              return this.file_type.indexOf(value) >= 0;
            }
          }
        },
        {
          text: this.$i18n.t("message.common.size") + "(KB)",
          value: 'size',
          align: 'right'
        },
        { text: this.$i18n.t("message.common.ctime"), value: 'ctime' },
        {
          text: this.$i18n.t("message.common.remark"),
          value: 'remarks',
          sortable: false,
          filter: value => {
            if(value === null
              || (typeof this.file_type === "string" && this.file_type !== "translated")
              || (Array.isArray(this.file_type) && this.file_type.indexOf("translated") < 0)
              || this.lang_pair === null
              || this.lang_pair.length !== 2
              || !('source_lang' in value)
              || !('target_lang' in value)
            ){
              return true;
            }
            return ((this.lang_pair[0] === value.source_lang)
                && (this.lang_pair[1] === value.target_lang))
              || ((this.lang_pair[1] === value.source_lang)
                  && (this.lang_pair[0] === value.target_lang));
          }
        },
        {
          text: this.$i18n.t("message.common.operation"),
          value: 'operation',
          sortable: false
        },
      ];
    },
    downloadable: function(){
      return function(extensions){
        var new_extensions = [];
        for(var extension of extensions){
          if(["cnt", "sim", "json"].indexOf(extension) === -1){
            new_extensions.push(extension);
          }
        }
        return new_extensions;
      }
    },
    previewable: function(){
      return function(extensions){
        var new_extensions = [];
        for(var extension of extensions){
          if(['txt', 'docx', 'xlsx', 'pptx', 'pdf', 'ocr', 'json'].indexOf(extension) >= 0){
            new_extensions.push(extension);
          }
        }
        return new_extensions;
      }
    }
  }
}
</script>
